import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Paper,
  IconButton,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Tabs,
  Tab,
  InputAdornment,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  AutoAwesome as AutoAwesomeIcon,
  AccountBalance as AccountBalanceIcon,
  Storefront as StorefrontIcon,
  MenuBook as MenuBookIcon,
  EmojiObjects as EmojiObjectsIcon,
  Security as SecurityIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Bookmark as BookmarkIcon,
  InfoOutlined as InfoOutlinedIcon,
  Description as DescriptionIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import ProModal from '../../common/ProModal';

// TabPanel component for the dialog
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`system-tabpanel-${index}`}
      aria-labelledby={`system-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SystemsEditor = ({ 
  systems = [], 
  onAddSystem, 
  onEditSystem, 
  onDeleteSystem 
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const safeSystems = Array.isArray(systems) ? systems : [];
  const cardBg = isDarkMode ? 'rgba(10, 30, 15, 0.92)' : '#fff';
  const accentColor = isDarkMode ? '#b9f6ca' : '#1b5e20';
  const accentBg = isDarkMode ? 'rgba(46, 125, 50, 0.2)' : 'rgba(76, 175, 80, 0.05)';
  const accentBorder = isDarkMode ? 'rgba(185, 246, 202, 0.45)' : 'rgba(76, 175, 80, 0.15)';
  const chipBg = isDarkMode ? 'rgba(185, 246, 202, 0.15)' : 'rgba(76, 175, 80, 0.1)';
  const chipHover = isDarkMode ? 'rgba(185, 246, 202, 0.25)' : 'rgba(76, 175, 80, 0.18)';
  const sectionBg = isDarkMode ? 'rgba(34, 94, 50, 0.35)' : 'rgba(76, 175, 80, 0.05)';
  const sectionBorder = isDarkMode ? 'rgba(185, 246, 202, 0.35)' : 'rgba(76, 175, 80, 0.15)';
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [showSystemDialog, setShowSystemDialog] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [selectedSystem, setSelectedSystem] = useState(null);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [currentSystem, setCurrentSystem] = useState({
    name: '',
    description: '',
    details: '',
    tags: [],
    importance: 'medium',
    category: 'systems',
    rules: '',
    mechanics: '',
    purpose: '',
    limitations: '',
    systemType: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [dialogTabValue, setDialogTabValue] = useState(0);

  // Define system templates for quick creation - optimized for film/screenwriting
  const systemTemplates = [
    { 
      name: 'magic', 
      label: 'Magic System', 
      icon: <AutoAwesomeIcon />,
      description: 'The rules that govern the impossible—and make it believable',
      rules: 'What triggers magic? Words, gestures, will, sacrifice? The rules must be clear enough that the audience understands stakes.',
      mechanics: 'Cost and consequence. Every spell should cost something—energy, time, sanity, years of life. The bigger the magic, the bigger the price.',
      purpose: 'Magic exists to create wonder and raise stakes. It should solve some problems while creating others.',
      limitations: 'The limitations ARE the story. What magic can\'t do matters more than what it can. Iron blocks it? Sunlight weakens it? Love breaks it?',
      details: '**Cinematic Moments:** The first spell cast. Magic going wrong. The moment someone pays the ultimate price. The duel of wills.\n\n**Conflict Hooks:** Magic is fading from the world. Someone is breaking the rules. A new form of magic emerges.',
      mood: 'Wonder and danger. The seduction of power. The price of the impossible.',
      visualStyle: 'Consistent visual language for magic. Color coding, particle effects, the physical toll on the caster.'
    },
    { 
      name: 'government', 
      label: 'Power Structure', 
      icon: <AccountBalanceIcon />,
      description: 'Who rules, how they rule, and why anyone obeys',
      rules: 'Succession, legitimacy, law. The rules everyone follows—and the ones the powerful break.',
      mechanics: 'How power flows. Bureaucracy, military, money, tradition. The levers that move society.',
      purpose: 'Creates the political landscape characters navigate. Defines who has power and who wants it.',
      limitations: 'Every government has cracks. Corruption, incompetence, distance from the people. Where the system fails is where stories happen.',
      details: '**Cinematic Moments:** The throne room confrontation. The secret meeting. The coup. The moment the system fails someone.\n\n**Conflict Hooks:** Succession crisis. Rebellion brewing. The ruler is compromised. Two legitimate claims to power.',
      mood: 'Power and its corruptions. The weight of rule. The gap between law and justice.',
      visualStyle: 'Architecture of power. The throne, the council chamber, the prison. Symbols of authority.'
    },
    { 
      name: 'economy', 
      label: 'Economic System', 
      icon: <StorefrontIcon />,
      description: 'What has value, who controls it, and what people will do for it',
      rules: 'Currency, trade laws, property rights. The rules of the game everyone plays.',
      mechanics: 'Markets, guilds, monopolies. How wealth moves and accumulates. Who profits and who pays.',
      purpose: 'Creates motivation. Money drives characters, factions, and conflicts. Scarcity creates drama.',
      limitations: 'Economic systems create winners and losers. The losers have grievances. The winners have enemies.',
      details: '**Cinematic Moments:** The deal gone wrong. The heist. The market crash. The moment someone sells their soul.\n\n**Conflict Hooks:** Resource runs out. New resource discovered. Economic collapse. Someone corners the market.',
      mood: 'Greed and desperation. The things people do for money. Wealth as power.',
      visualStyle: 'Contrast between wealth and poverty. The marketplace, the vault, the slum. Money changing hands.'
    },
    { 
      name: 'religion', 
      label: 'Faith System', 
      icon: <MenuBookIcon />,
      description: 'What people believe, why they believe it, and what they\'ll do for it',
      rules: 'Commandments, rituals, hierarchy. The rules believers follow—and the heresies they commit.',
      mechanics: 'Worship, priesthood, sacred sites. How faith is practiced and organized.',
      purpose: 'Provides meaning, community, and motivation. Faith can inspire heroism or justify atrocity.',
      limitations: 'Doubt, schism, hypocrisy. The gap between doctrine and practice. When faith fails.',
      details: '**Cinematic Moments:** The crisis of faith. The miracle. The sacrifice. The moment the god answers—or doesn\'t.\n\n**Conflict Hooks:** Competing faiths. The church is corrupt. The god is real—and not what they expected. Prophecy coming true.',
      mood: 'Devotion and doubt. The comfort and terror of belief. Faith tested.',
      visualStyle: 'Sacred spaces, religious symbols, ritual and ceremony. The faithful and the fallen.'
    },
    { 
      name: 'social', 
      label: 'Social Hierarchy', 
      icon: <AccountBalanceIcon />,
      description: 'Who matters, who doesn\'t, and the invisible walls between them',
      rules: 'Class, caste, status. The rules that determine who can go where, do what, marry whom.',
      mechanics: 'How status is gained, lost, displayed. The markers of class. The paths of mobility.',
      purpose: 'Creates barriers and aspirations. Characters fight against their place or defend their privilege.',
      limitations: 'Every hierarchy creates resentment. Those at the bottom want up. Those at the top fear falling.',
      details: '**Cinematic Moments:** Crossing class lines. The humiliation. The rise. The fall. The moment someone is seen for who they really are.\n\n**Conflict Hooks:** Forbidden love across classes. Revolution from below. Imposter among the elite. The system exposed.',
      mood: 'Aspiration and resentment. The weight of expectation. Breaking free or fitting in.',
      visualStyle: 'Visual markers of class—clothing, spaces, body language. The contrast between worlds.'
    },
    { 
      name: 'justice', 
      label: 'Justice System', 
      icon: <SecurityIcon />,
      description: 'How wrongs are judged and punished—and who decides what\'s wrong',
      rules: 'Laws, courts, punishments. The official rules—and the unofficial ones.',
      mechanics: 'Investigation, trial, enforcement. How justice is pursued and delivered.',
      purpose: 'Creates stakes for actions. Characters fear punishment or seek justice.',
      limitations: 'Justice is never perfect. The innocent are punished. The guilty escape. The system serves power.',
      details: '**Cinematic Moments:** The trial. The execution. The escape. The moment justice fails or triumphs.\n\n**Conflict Hooks:** Wrongly accused. The guilty going free. Vigilante justice. The law versus what\'s right.',
      mood: 'Justice and vengeance. The weight of judgment. Law versus morality.',
      visualStyle: 'Courtrooms, prisons, the execution. The accused and the accuser. Symbols of law.'
    },
    { 
      name: 'honor', 
      label: 'Honor Code', 
      icon: <SecurityIcon />,
      description: 'The unwritten rules that matter more than law',
      rules: 'What honor demands. Debts, oaths, challenges. The things that must be done.',
      mechanics: 'How honor is gained and lost. Duels, vendettas, redemption. The rituals of reputation.',
      purpose: 'Creates obligations that override self-interest. Characters do things because honor demands it.',
      limitations: 'Honor can demand terrible things. It can trap people in cycles of violence. It can be manipulated.',
      details: '**Cinematic Moments:** The duel. The oath sworn. The debt repaid. The moment honor demands the impossible.\n\n**Conflict Hooks:** Honor versus love. Impossible oath. Family honor tainted. The honorable choice is the wrong one.',
      mood: 'Duty and sacrifice. The weight of reputation. Pride and its costs.',
      visualStyle: 'Ritual confrontations. The formal challenge. Symbols of honor worn and displayed.'
    },
    { 
      name: 'survival', 
      label: 'Survival Rules', 
      icon: <SecurityIcon />,
      description: 'The harsh realities that govern life in dangerous places',
      rules: 'What kills you and how fast. Food, water, shelter, threats. The non-negotiable needs.',
      mechanics: 'Scarcity, rationing, territory. How survival is managed day to day.',
      purpose: 'Creates constant tension. Every decision matters when resources are scarce.',
      limitations: 'Survival pushes people to extremes. Morality becomes luxury. Desperation breeds conflict.',
      details: '**Cinematic Moments:** The last of the supplies. The desperate choice. Finding salvation. Losing someone to the environment.\n\n**Conflict Hooks:** Resources running out. Safe haven isn\'t safe. Someone hoarding. The choice of who lives.',
      mood: 'Desperation and resilience. The things people do to survive. Hope in harsh places.',
      visualStyle: 'Environmental hostility. Scarcity visible. The toll on bodies and minds.'
    },
    { 
      name: 'information', 
      label: 'Information Control', 
      icon: <EmojiObjectsIcon />,
      description: 'Who knows what, who controls the truth, and what secrets cost',
      rules: 'What\'s public, what\'s secret, what\'s forbidden. The rules of knowledge.',
      mechanics: 'How information flows or is blocked. Spies, censors, networks. The infrastructure of secrets.',
      purpose: 'Creates asymmetry. Characters with information have power. Those without are vulnerable.',
      limitations: 'Secrets always threaten to escape. The truth has a way of emerging. Controlling information requires constant effort.',
      details: '**Cinematic Moments:** The revelation. The leak. The cover-up exposed. The moment someone learns what they shouldn\'t.\n\n**Conflict Hooks:** Dangerous secret discovered. Information broker playing sides. The truth would destroy everything. Someone knows too much.',
      mood: 'Paranoia and revelation. The power of secrets. Truth as weapon.',
      visualStyle: 'Hidden meetings, coded messages, the moment of discovery. What\'s seen versus what\'s known.'
    },
    { 
      name: 'custom', 
      label: 'Custom System', 
      icon: <AddCircleOutlineIcon />,
      description: 'Create your own unique system from scratch',
      rules: '',
      mechanics: '',
      purpose: '',
      limitations: '',
      details: '',
      mood: '',
      visualStyle: ''
    }
  ];

  const handleSaveTemplate = () => {
    if (!templateName.trim()) return;
    
    const newTemplate = {
      name: templateName.trim(),
      label: templateName.trim(),
      description: templateDescription.trim(),
      icon: <SettingsIcon />,
      rules: selectedSystem?.rules || '',
      mechanics: selectedSystem?.mechanics || '',
      purpose: selectedSystem?.purpose || '',
      limitations: selectedSystem?.limitations || ''
    };
    
    setCustomTemplates([...customTemplates, newTemplate]);
    setTemplateName('');
    setTemplateDescription('');
    setSelectedSystem(null);
    setShowSaveTemplateDialog(false);
  };
  
  const deleteCustomTemplate = (templateName) => {
    setCustomTemplates(customTemplates.filter(template => template.name !== templateName));
  };
  
  const applyCustomTemplate = (templateName) => {
    const template = customTemplates.find(t => t.name === templateName);
    if (template) {
      setCurrentSystem({
        name: '',
        description: template.description,
        details: '',
        tags: [],
        importance: 'medium',
        category: 'systems',
        rules: template.rules,
        mechanics: template.mechanics,
        purpose: template.purpose,
        limitations: template.limitations,
        systemType: 'custom'
      });
    }
  };
  
  const applySystemTemplate = (template) => {
    // Build cinematic details from new fields
    const cinematicDetails = (template.details || '') + 
      (template.mood ? `\n\n**Mood:** ${template.mood}` : '') +
      (template.visualStyle ? `\n\n**Visual Style:** ${template.visualStyle}` : '');
    
    setCurrentSystem({
      name: '',
      description: template.description,
      details: cinematicDetails,
      tags: [],
      importance: 'medium',
      category: 'systems',
      rules: template.rules,
      mechanics: template.mechanics,
      purpose: template.purpose,
      limitations: template.limitations,
      systemType: template.name
    });
  };
  
  const handleOpenAddSystem = () => {
    setIsEditing(false);
    setCurrentSystem({
      name: '',
      description: '',
      details: '',
      tags: [],
      importance: 'medium',
      category: 'systems',
      rules: '',
      mechanics: '',
      purpose: '',
      limitations: '',
      systemType: ''
    });
    setShowSystemDialog(true);
  };
  
  const handleOpenEditSystem = (system) => {
    setIsEditing(true);
    setCurrentSystem({
      ...system,
      tags: Array.isArray(system.tags) ? system.tags : []
    });
    setShowSystemDialog(true);
  };
  
  const handleSystemChange = (field, value) => {
    setCurrentSystem({
      ...currentSystem,
      [field]: value
    });
  };
  
  const handleAddTag = () => {
    if (newTag.trim() && !currentSystem.tags.includes(newTag.trim())) {
      setCurrentSystem({
        ...currentSystem,
        tags: [...currentSystem.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };
  
  const handleRemoveTag = (tagToRemove) => {
    setCurrentSystem({
      ...currentSystem,
      tags: currentSystem.tags.filter(tag => tag !== tagToRemove)
    });
  };
  
  const handleSaveSystem = () => {
    if (!currentSystem.name.trim()) return;
    
    const systemToSave = {
      ...currentSystem,
      id: currentSystem.id || Date.now().toString()
    };
    
    if (isEditing) {
      onEditSystem(systemToSave);
    } else {
      onAddSystem(systemToSave);
    }
    
    setShowSystemDialog(false);
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Tooltip title="Add new system" arrow>
          <span>
            <IconButton
              onClick={handleOpenAddSystem}
              aria-label="Add new system"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                color: accentColor,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: accentBg
                }
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="h5" component="h2" sx={{ 
          fontWeight: 600,
          color: accentColor
        }}>
          Rules & Systems
        </Typography>
      </Box>

      {safeSystems.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            textAlign: 'center',
            borderRadius: 2,
            border: `1px dashed ${accentBorder}`,
            bgcolor: isDarkMode ? 'rgba(46, 125, 50, 0.2)' : 'rgba(76, 175, 80, 0.07)'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: accentColor }}>
            No systems added yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tap the “+” icon to add your first rule or system and shape how your world works.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {safeSystems.map(system => (
            <Grid item xs={12} sm={6} md={4} key={system.id}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: isDarkMode ? '0 12px 30px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.08)',
                transition: 'transform 0.3s, box-shadow 0.3s',
                border: `1px solid ${isDarkMode ? 'rgba(185,246,202,0.25)' : 'rgba(0,0,0,0.05)'}`,
                backgroundColor: cardBg,
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: isDarkMode ? '0 18px 35px rgba(0,0,0,0.65)' : '0 12px 20px rgba(0,0,0,0.12)'
                }
              }}>
                <Box sx={{ 
                  p: 1, 
                  background: isDarkMode ? 'linear-gradient(135deg, rgba(76,175,80,0.35), rgba(56,142,60,0.15))' : 'linear-gradient(135deg, #4caf5040, #4caf5010)',
                  borderBottom: `1px solid ${isDarkMode ? 'rgba(185,246,202,0.25)' : 'rgba(0,0,0,0.05)'}`
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600,
                      color: accentColor
                    }}>
                      {system.name}
                    </Typography>
                    {system.importance && (
                      <Chip 
                        size="small" 
                        label={system.importance.charAt(0).toUpperCase() + system.importance.slice(1)} 
                        color={
                          system.importance === 'high' ? 'error' : 
                          system.importance === 'medium' ? 'primary' : 
                          'default'
                        }
                        sx={{ 
                          fontWeight: 500,
                          height: '22px'
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <Box sx={{ p: 2, flexGrow: 1 }}>
                  {system.description && (
                    <Typography variant="body2" sx={{ mb: 1, color: isDarkMode ? 'rgba(255,255,255,0.8)' : 'text.secondary' }}>
                      {system.description}
                    </Typography>
                  )}
                
                  {system.purpose && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" sx={{ mr: 0.5, fontWeight: 600, color: accentColor }}>
                        Purpose:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        color: isDarkMode ? 'rgba(255,255,255,0.85)' : 'text.secondary'
                      }}>
                        {system.purpose}
                      </Typography>
                    </Box>
                  )}
                
                  {system.limitations && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="body2" sx={{ mr: 0.5, fontWeight: 600, color: accentColor }}>
                        Limitations:
                      </Typography>
                      <Typography variant="body2" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        color: isDarkMode ? 'rgba(255,255,255,0.85)' : 'text.secondary'
                      }}>
                        {system.limitations}
                      </Typography>
                    </Box>
                  )}
                
                  {system.tags && system.tags.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {system.tags.map((tag, index) => (
                        <Chip 
                          key={index} 
                          label={tag} 
                          size="small" 
                          sx={{ 
                            height: '20px', 
                            fontSize: '0.7rem',
                            bgcolor: chipBg,
                            color: accentColor
                          }} 
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              
                {/* Template Info */}
                {system.systemType && system.systemType !== 'custom' && (
                  <Paper 
                    elevation={0} 
                    sx={{ 
                      p: 1.5, 
                      mx: 2,
                      mb: 2,
                      borderRadius: 1, 
                      bgcolor: chipBg,
                      border: '1px dashed',
                      borderColor: accentColor
                    }}
                  >
                    <Typography variant="caption" sx={{ color: accentColor }}>
                      {systemTemplates.find(t => t.name === system.systemType)?.label || 'System'} Template
                    </Typography>
                  </Paper>
                )}
              
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  p: 1, 
                  pt: 0,
                  borderTop: '1px solid rgba(0,0,0,0.05)'
                }}>
                  <Button 
                    size="small" 
                    startIcon={<EditIcon />} 
                    onClick={() => handleOpenEditSystem(system)}
                    sx={{ mr: 1 }}
                  >
                    Edit
                  </Button>
                  <Button 
                    size="small" 
                    startIcon={<DeleteIcon />} 
                    color="error"
                    onClick={() => onDeleteSystem(system.id)}
                  >
                    Delete
                  </Button>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      <ProModal
        open={showSystemDialog}
        onClose={() => setShowSystemDialog(false)}
        title={isEditing ? 'Edit System' : 'Add New System'}
        eyebrow="RULES & SYSTEMS"
        icon={<SettingsIcon />}
        maxWidth="lg"
        actions={(
          <>
            <Button 
              onClick={() => setShowSystemDialog(false)} 
              color="inherit"
              variant="outlined"
              startIcon={<CloseIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                mr: 1
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveSystem} 
              variant="contained" 
              startIcon={<SaveIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                background: 'linear-gradient(120deg, #43a047, #1b5e20)',
                '&:hover': {
                  background: 'linear-gradient(120deg, #2e7d32, #10451d)'
                }
              }}
            >
              {isEditing ? 'Update System' : 'Add System'}
            </Button>
          </>
        )}
        contentProps={{ sx: { pt: 3, px: 3 } }}
      >
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: sectionBg, borderRadius: 1, border: `1px solid ${sectionBorder}` }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <InfoOutlinedIcon sx={{ mr: 1, fontSize: '1rem' }} /> Basic Information
                </Typography>
              
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      autoFocus
                      margin="dense"
                      label="System Name"
                      fullWidth
                      variant="outlined"
                      value={currentSystem.name}
                      onChange={(e) => handleSystemChange('name', e.target.value)}
                      required
                      placeholder="Enter a name for this system"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="dense" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                      <InputLabel id="importance-label">Importance</InputLabel>
                      <Select
                        labelId="importance-label"
                        value={currentSystem.importance}
                        label="Importance"
                        onChange={(e) => handleSystemChange('importance', e.target.value)}
                      >
                        <MenuItem value="low">Minor System</MenuItem>
                        <MenuItem value="medium">Supporting System</MenuItem>
                        <MenuItem value="high">Major System</MenuItem>
                      </Select>
                      <FormHelperText>
                        How significant is this system to your world?
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      margin="dense"
                      label="Description"
                      fullWidth
                      multiline
                      rows={2}
                      variant="outlined"
                      value={currentSystem.description}
                      onChange={(e) => handleSystemChange('description', e.target.value)}
                      placeholder="A logline for this system—what is it and why does it matter to your story?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            {/* System Rules & Mechanics */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, mb: 1, bgcolor: sectionBg, borderRadius: 1, border: `1px solid ${sectionBorder}` }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <SettingsIcon sx={{ mr: 1, fontSize: '1rem' }} /> System Rules & Mechanics
                </Typography>
              
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      margin="dense"
                      label="Rules"
                      fullWidth
                      multiline
                      rows={2}
                      variant="outlined"
                      value={currentSystem.rules}
                      onChange={(e) => handleSystemChange('rules', e.target.value)}
                      placeholder="What are the rules the audience needs to understand? Clear rules create clear stakes."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      margin="dense"
                      label="Mechanics"
                      fullWidth
                      multiline
                      rows={2}
                      variant="outlined"
                      value={currentSystem.mechanics}
                      onChange={(e) => handleSystemChange('mechanics', e.target.value)}
                      placeholder="How does it actually function? The mechanics create opportunities for drama."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            {/* System Purpose & Limitations */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, mb: 1, bgcolor: 'rgba(76, 175, 80, 0.05)', borderRadius: '8px', border: '1px solid rgba(76, 175, 80, 0.1)' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#1b5e20', display: 'flex', alignItems: 'center' }}>
                  <MenuBookIcon sx={{ mr: 1, fontSize: '1rem' }} /> Purpose & Limitations
                </Typography>
              
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="dense"
                      label="Purpose"
                      fullWidth
                      multiline
                      rows={2}
                      variant="outlined"
                      value={currentSystem.purpose}
                      onChange={(e) => handleSystemChange('purpose', e.target.value)}
                      placeholder="Why does this system exist in your story? What conflicts does it enable?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      margin="dense"
                      label="Limitations"
                      fullWidth
                      multiline
                      rows={2}
                      variant="outlined"
                      value={currentSystem.limitations}
                      onChange={(e) => handleSystemChange('limitations', e.target.value)}
                      placeholder="Limitations create drama. Where does the system fail? Who exploits it?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            {/* Additional Details & Tags */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, mb: 1, bgcolor: 'rgba(76, 175, 80, 0.05)', borderRadius: '8px', border: '1px solid rgba(76, 175, 80, 0.1)' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#1b5e20', display: 'flex', alignItems: 'center' }}>
                  <DescriptionIcon sx={{ mr: 1, fontSize: '1rem' }} /> Additional Details
                </Typography>
              
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      margin="dense"
                      label="Details"
                      fullWidth
                      multiline
                      rows={2}
                      variant="outlined"
                      value={currentSystem.details}
                      onChange={(e) => handleSystemChange('details', e.target.value)}
                      placeholder="Additional details about this system"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: accentColor }}>Tags</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TextField
                        margin="dense"
                        label="Tags"
                        variant="outlined"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        placeholder="Add tags"
                        size="small"
                        sx={{ mr: 1, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      />
                      <Button 
                        variant="outlined" 
                        size="small" 
                        onClick={handleAddTag}
                        disabled={!newTag.trim()}
                        sx={{ borderRadius: '8px' }}
                      >
                        Add
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {currentSystem.tags.map((tag, index) => (
                        <Chip 
                          key={index} 
                          label={tag} 
                          size="small" 
                          onDelete={() => handleRemoveTag(tag)}
                          sx={{ 
                            bgcolor: chipBg,
                            color: accentColor,
                            borderRadius: '4px'
                          }} 
                        />
                      ))}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            {/* System Templates */}
            <Grid item xs={12}>
              <Accordion 
                defaultExpanded={false}
                sx={{ 
                  bgcolor: sectionBg, 
                  borderRadius: '1 !important', 
                  border: `1px solid ${sectionBorder}`,
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                  mb: 2
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon sx={{ color: accentColor }} />}
                  sx={{ 
                    minHeight: 48,
                    '& .MuiAccordionSummary-content': { my: 1 }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                    <SettingsIcon sx={{ mr: 1, fontSize: '1rem' }} /> Quick Templates
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary', fontStyle: 'italic' }}>
                    Apply a template to quickly populate your system with common characteristics
                  </Typography>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Grid container spacing={1}>
                        {systemTemplates.map((template) => (
                          <Grid item xs={6} sm={4} md={3} key={template.name}>
                            <Card 
                              variant="outlined" 
                              sx={{ 
                                p: 1, 
                                cursor: 'pointer',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                borderRadius: '8px',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                                }
                              }}
                              onClick={() => applySystemTemplate(template)}
                            >
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                bgcolor: 'rgba(76, 175, 80, 0.1)',
                                mb: 1
                              }}>
                                {template.icon}
                              </Box>
                              <Typography variant="caption" sx={{ textAlign: 'center', fontWeight: 500 }}>
                                {template.label}
                              </Typography>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
            
            {/* Custom Templates */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.03)', borderRadius: '8px', border: '1px solid rgba(76, 175, 80, 0.08)' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1b5e20', display: 'flex', alignItems: 'center' }}>
                    <BookmarkIcon sx={{ mr: 1, fontSize: '1rem' }} /> Your Custom Templates
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setSelectedSystem(currentSystem);
                      setShowSaveTemplateDialog(true);
                    }}
                    sx={{ 
                      borderRadius: '8px',
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #4caf50 30%, #2e7d32 90%)',
                    }}
                  >
                    Save Current as Template
                  </Button>
                </Box>
                
                {customTemplates.length > 0 ? (
                  <Grid container spacing={2}>
                    {customTemplates.map((template) => (
                      <Grid item key={template.name} xs={12} sm={6} md={4}>
                        <Card sx={{ 
                          position: 'relative',
                          cursor: 'pointer',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          borderRadius: '8px',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                          }
                        }}
                        onClick={() => applyCustomTemplate(template.name)}
                        >
                          <Box sx={{ 
                            p: 2, 
                            display: 'flex', 
                            alignItems: 'center',
                            borderBottom: '1px solid rgba(0,0,0,0.08)',
                            bgcolor: 'rgba(76, 175, 80, 0.05)'
                          }}>
                            <Box sx={{ 
                              mr: 1, 
                              display: 'flex', 
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 32,
                              height: 32,
                              borderRadius: '50%',
                              bgcolor: 'rgba(76, 175, 80, 0.1)'
                            }}>
                              {template.icon ? React.cloneElement(template.icon, { fontSize: 'small' }) : <SettingsIcon fontSize="small" />}
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {template.label}
                            </Typography>
                            <Box sx={{ ml: 'auto' }}>
                              <IconButton 
                                size="small" 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteCustomTemplate(template.name);
                                }}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                          <CardContent sx={{ flexGrow: 1, p: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              {template.description || 'Custom system template'}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ p: 2, textAlign: 'center', border: '1px dashed rgba(76, 175, 80, 0.3)', borderRadius: '8px' }}>
                    <Typography variant="body2" color="text.secondary">
                      No custom templates yet. Create a system and save it as a template to reuse later.
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
      </ProModal>
      
      <ProModal
        open={showSaveTemplateDialog}
        onClose={() => setShowSaveTemplateDialog(false)}
        title="Save as Template"
        eyebrow="RULES & SYSTEMS"
        icon={<BookmarkIcon />}
        maxWidth="sm"
        actions={(
          <>
            <Button 
              onClick={() => setShowSaveTemplateDialog(false)}
              color="inherit"
              variant="outlined"
              startIcon={<CloseIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                mr: 1
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTemplate}
              variant="contained"
              disabled={!templateName.trim()}
              startIcon={<SaveIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                background: 'linear-gradient(120deg, #43a047, #1b5e20)',
                '&:hover': {
                  background: 'linear-gradient(120deg, #2e7d32, #10451d)'
                }
              }}
            >
              Save
            </Button>
          </>
        )}
      >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Save the current system as a template for future use.
          </Typography>
          
          <TextField
            margin="dense"
            label="Template Name"
            fullWidth
            variant="outlined"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            required
            placeholder="Enter a descriptive name for your template"
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <BookmarkIcon sx={{ color: '#e65100' }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value)}
            placeholder="Describe what makes this system template unique or useful"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DescriptionIcon sx={{ color: '#e65100' }} />
                </InputAdornment>
              ),
            }}
          />
        </ProModal>
    </Box>
  );
};

export default SystemsEditor;
